import { Button, Grid, Stack, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useRef, useState } from 'react'
import axiosClient from 'utils/axiosClient'
import { loadImageProcessToCanvas } from 'utils/imageConvert'
import DialogConfirm from './Dialogs/DialogConfirm'

interface Props {
  src?: string
  apiEndpoint?: string
  fieldName?: string
  maxWidth?: number
  canDelete?: boolean
}
export default function ShowAndReUploadImage({
  src,
  apiEndpoint,
  fieldName,
  maxWidth = 300,
  canDelete = false,
}: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const inputRef = useRef({} as HTMLInputElement)
  const [image, setImage] = useState<HTMLCanvasElement>()
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const handleConfirm = async () => {
    inputRef.current.value = ''
    if (fieldName && apiEndpoint) {
      try {
        setLoading(true)
        let formData = new FormData()
        formData.append(
          'data',
          JSON.stringify({
            ['remove_' + fieldName]: true,
          })
        )
        await axiosClient.put(apiEndpoint, formData)
        setImage(undefined)
        const msg = '画像削除しました。'
        enqueueSnackbar(msg, {
          variant: 'success',
        })
        setLoading(false)
        setShowConfirm(false)
      } catch (_error) {
        const msg = 'エラーが発生しました。'
        enqueueSnackbar(msg, {
          variant: 'error',
        })
      }
    }
  }
  return (
    <Stack my={2} direction="row" spacing={2}>
      <DialogConfirm
        loading={loading}
        open={showConfirm}
        handleClose={() => setShowConfirm(false)}
        handleSubmit={handleConfirm}
        primaryButtonText="削除する"
        primaryButtonColor="error"
      >
        画像を削除してもよろしいでしょうか
      </DialogConfirm>
      <input
        ref={inputRef}
        accept="image/*"
        style={{ display: 'none' }}
        id={fieldName || 'upload'}
        type="file"
        onChange={async (e) => {
          if (e.target.files) {
            setImage(await loadImageProcessToCanvas(e.target.files[0]))
            inputRef.current.value = ''
          }
        }}
      />

      {image ? (
        <Stack
          sx={{ ml: '0 !important' }}
          width="100%"
          spacing={1}
          justifyContent="center"
          alignItems="center"
        >
          <img
            width={image.width}
            src={image.toDataURL()}
            alt="画像"
            style={{
              maxWidth,
              border: '1px solid #eee',
            }}
          ></img>
          <Grid container spacing={1} mt={2}>
            <Grid item xs={6} md={6} textAlign="center">
              <Button
                variant="contained"
                component="span"
                size="small"
                color="info"
                onClick={() => {
                  inputRef.current.value = ''
                  setImage(undefined)
                }}
                sx={{ width: 'max-content', px: 2, marginLeft: 0 }}
              >
                戻る
              </Button>
            </Grid>
            <Grid item xs={6} md={6} textAlign="center">
              <Button
                variant="contained"
                component="span"
                size="small"
                color="warning"
                onClick={async () => {
                  inputRef.current.value = ''
                  if (fieldName && apiEndpoint) {
                    try {
                      let formData = new FormData()
                      if (image)
                        await new Promise((rs) => {
                          image.toBlob((blob) => {
                            if (blob) {
                              const ext = blob.type.split('/').pop()
                              let file = new File(
                                [blob],
                                'answeredImage.' + ext,
                                {
                                  type: blob.type,
                                }
                              )
                              formData.append(fieldName, file)
                              rs(1)
                            }
                          })
                        })
                      formData.append('data', '{}')
                      await axiosClient.put(apiEndpoint, formData)
                      setImage(undefined)
                      const msg = '画像のアップロードが完了しました。'
                      enqueueSnackbar(msg, {
                        variant: 'success',
                      })
                    } catch (_error) {
                      const msg = 'エラーが発生しました。'
                      enqueueSnackbar(msg, {
                        variant: 'error',
                      })
                    }
                  }
                }}
                sx={{ width: 'max-content', px: 2, marginLeft: 0 }}
              >
                決定する
              </Button>
            </Grid>
          </Grid>
        </Stack>
      ) : (
        <Stack spacing={1}>
          {!!src && (
            <img
              src={src}
              alt="画像"
              style={{
                maxWidth,
                border: '1px solid #eee',
              }}
            ></img>
          )}
          {!src && <Typography sx={{ color: '#ccc' }}>(画像無し)</Typography>}
          <Stack direction="row" spacing={2}>
            <label
              htmlFor={fieldName || 'upload'}
              style={{ width: 'max-content', marginLeft: 0 }}
            >
              <Button
                id="upload-image-btn"
                variant="contained"
                component="span"
                size="small"
                color="info"
                sx={{
                  px: 2,
                }}
              >
                {src ? `再アップロード` : 'アップロード'}
              </Button>
            </label>
            {canDelete && src && (
              <Button
                variant="contained"
                component="span"
                size="small"
                color="warning"
                sx={{
                  px: 2,
                }}
                onClick={() => setShowConfirm(true)}
              >
                削除する
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}
